<template lang="pug">
yz-filter-menu(:label="$t('panel.visit_date')" :is-active="isActive" :display-value="displayValue" :disable-apply="!isValid" @clear="onClear" @apply="onApply")
  .pa-4
    yz-dropdown.mb-3(v-model="option" :items="options" item-value="id" item-text="name" size="lg")

    .d-flex.align-start
      v-icon.mx-2(size=16 color="primary") $yzi-corner-down-right

      .flex-grow-1
        template(v-if="option === 'duration'")
          .d-flex.align-center.gap-2
            yz-text-field(v-model="durationValue" hide-details :placeholder="$t('value')" type="number" style="flex: 1")
            yz-dropdown.ml-2(v-model="duration" :items="durations" item-value="id" item-text="name" size="lg" style="flex: 1")

        template(v-else-if="option === 'between'")
          .d-flex.flex-column
            yz-menu(v-model="showStartDateMenu" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto")
              template(v-slot:activator="{ on, attrs }")
                yz-text-field(:value="formattedStartDate" :placeholder="$t('panel.start_date')" prepend-inner-icon="$yzi-calendar" readonly clickable clearable hide-details v-bind="attrs" v-on="on" @click:clear="value = null")
              yz-date-picker(v-model="value" :max="endValue || undefined" scrollable @input="showStartDateMenu = false")

            .text-xs.my-2.mx-2.neutral500--text.text-lowercase {{ $t('to') }}

            yz-menu(v-model="showEndDateMenu" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto")
              template(v-slot:activator="{ on, attrs }")
                yz-text-field(:value="formattedEndDate" :placeholder="$t('panel.end_date')" prepend-inner-icon="$yzi-calendar" readonly clickable clearable hide-details v-bind="attrs" v-on="on" @click:clear="endValue = null")
              yz-date-picker(v-model="endValue" :min="value || undefined" scrollable @input="showEndDateMenu = false")

        template(v-else)
          yz-menu(v-model="showDateMenu" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto")
            template(v-slot:activator="{ on, attrs }")
              yz-text-field(:value="formattedSingleDate" :placeholder="$t('panel.select_date')" prepend-inner-icon="$yzi-calendar" readonly clickable clearable hide-details v-bind="attrs" v-on="on" @click:clear="value = null")
            yz-date-picker(v-model="value" @input="showDateMenu = false" scrollable)
</template>

<script>
import { DateTime } from 'luxon'

export default {
  name: 'FilterVisitDate',
  props: {
    filter: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      option: 'visit_date',
      duration: 'day',
      durationValue: null,
      value: null,
      endValue: null,
      selectedOption: null,
      selectedValue: null,
      selectedEndValue: null,
      selectedDurationValue: null,
      showDateMenu: false,
      showStartDateMenu: false,
      showEndDateMenu: false,
      isActive: false,
      displayValue: '',
      options: [
        { id: 'visit_date', name: this.$t('panel.is_equal_to') },
        { id: 'duration', name: this.$t('panel.is_in_the_last') },
        { id: 'between', name: this.$t('panel.is_between') },
        { id: 'visit_date__gt', name: this.$t('panel.is_after') },
        { id: 'visit_date__gte', name: this.$t('panel.is_on_or_after') },
        { id: 'visit_date__lt', name: this.$t('panel.is_before') },
        { id: 'visit_date__lte', name: this.$t('panel.is_before_or_on') }
      ],
      durations: [
        { id: 'day', name: this.$t('panel.days') },
        { id: 'week', name: this.$t('panel.weeks') },
        { id: 'month', name: this.$t('panel.months') },
        { id: 'year', name: this.$t('panel.years') }
      ]
    }
  },
  computed: {
    isValid() {
      if (this.option === 'duration') {
        return !!this.durationValue && this.durationValue > 0
      }
      if (this.option === 'between') {
        return !!this.value && !!this.endValue
      }
      return !!this.value
    },
    formattedSingleDate() {
      if (!this.value) return ''
      return DateTime.fromISO(this.value).toFormat('dd/MM/yyyy')
    },
    formattedStartDate() {
      if (!this.value) return ''
      return DateTime.fromISO(this.value).toFormat('dd/MM/yyyy')
    },
    formattedEndDate() {
      if (!this.endValue) return ''
      return DateTime.fromISO(this.endValue).toFormat('dd/MM/yyyy')
    },
    selectedName() {
      const option = this.options.find(o => o.id === this.selectedOption)
      return option?.name || ''
    }
  },
  watch: {
    filter: {
      handler() {
        if (!this.filter.key) {
          this.clearData()
        }
      }
    }
  },
  methods: {
    onClear() {
      this.clearData()
      this.isActive = false
      this.displayValue = ''
      this.$emit('update:filter', {})
    },
    clearData() {
      this.option = 'visit_date'
      this.value = null
      this.endValue = null
      this.selectedOption = null
      this.selectedValue = null
      this.selectedEndValue = null
      this.durationValue = null
      this.selectedDurationValue = null
      this.duration = 'day'
    },
    onApply() {
      this.isActive = true
      this.selectedOption = this.option
      this.selectedValue = this.value
      this.selectedEndValue = this.endValue
      this.selectedDurationValue = this.durationValue

      let filter = {}
      let displayValue = ''

      if (this.option === 'between') {
        // Between two dates
        const startFormatted = DateTime.fromISO(this.value).toFormat('d MMM yyyy')
        const endFormatted = DateTime.fromISO(this.endValue).toFormat('d MMM yyyy')
        displayValue = `${startFormatted} - ${endFormatted}`

        filter = {
          key: 'between',
          value: [
            { key: 'visit_date__gte', value: this.value },
            { key: 'visit_date__lte', value: this.endValue }
          ],
          params: {
            visit_date__gte: this.value,
            visit_date__lte: this.endValue
          }
        }
      } else if (this.option === 'duration') {
        // Duration-based (last X days/weeks/months/years)
        const calculatedDate = this.getDateFromDuration()
        const today = DateTime.local().toISODate()
        const startFormatted = DateTime.fromISO(calculatedDate).toFormat('d MMM yyyy')
        const endFormatted = DateTime.fromISO(today).toFormat('d MMM yyyy')
        displayValue = `${startFormatted} - ${endFormatted} (${this.durationValue} ${this.duration}s)`

        filter = {
          key: 'visit_date__range',
          value: [calculatedDate, today],
          params: {
            visit_date__gte: calculatedDate,
            visit_date__lte: today
          }
        }
      } else {
        // Single date with operator (equal to, after, before, etc.)
        displayValue = DateTime.fromISO(this.value).toFormat('d MMM yyyy')

        filter = {
          key: this.option,
          value: this.value,
          params: {}
        }

        // Set the appropriate param based on operator
        filter.params[this.option] = this.value
      }

      this.displayValue = displayValue
      this.$emit('update:filter', filter)
    },
    getDateFromDuration() {
      let date = DateTime.local()

      switch (this.duration) {
        case 'day':
          date = date.minus({ days: parseInt(this.durationValue) })
          break
        case 'week':
          date = date.minus({ weeks: parseInt(this.durationValue) })
          break
        case 'month':
          date = date.minus({ months: parseInt(this.durationValue) })
          break
        case 'year':
          date = date.minus({ years: parseInt(this.durationValue) })
          break
      }

      return date.toISODate()
    },
    reset() {
      this.onClear()
    },
    restoreFromParams(params) {
      // Restore filter state from URL params
      // Check which type of filter was applied
      if (params.visit_date__gte && params.visit_date__lte && params.visit_date__gte === params.visit_date__lte) {
        // Equal to (exact date)
        this.option = 'visit_date'
        this.value = params.visit_date__gte
      } else if (params.visit_date__gte && params.visit_date__lte) {
        // Between two dates or duration (we can't distinguish, treat as between)
        this.option = 'between'
        this.value = params.visit_date__gte
        this.endValue = params.visit_date__lte
      } else if (params.visit_date__gt) {
        this.option = 'visit_date__gt'
        this.value = params.visit_date__gt
      } else if (params.visit_date__gte) {
        this.option = 'visit_date__gte'
        this.value = params.visit_date__gte
      } else if (params.visit_date__lt) {
        this.option = 'visit_date__lt'
        this.value = params.visit_date__lt
      } else if (params.visit_date__lte) {
        this.option = 'visit_date__lte'
        this.value = params.visit_date__lte
      } else if (params.visit_date) {
        this.option = 'visit_date'
        this.value = params.visit_date
      }

      // Apply to set isActive and displayValue
      if (this.isValid) {
        this.onApply()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.gap-2 {
  gap: 8px;
}
</style>
